/**
 * members.repository.ts — 법인 소속(tenants/{t}/members/{uid}) 읽기·쓰기 한 곳.
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 · §5-3 · §6-1 · §8-B(통합관리) · §8-C(법인 환경설정 명단).
 * 2026-10-23 통합: C 묶음(통합관리)과 D 묶음(tenant_members.repository — 공간 판정·법인 관리자 명단)을 합쳤다.
 *
 * Firestore 문서 ID = uid (비즈니스 키 login_id 와 다름). 행 key 는 `${tenant}:${uid}`.
 * rules: read = isAdmin || isTenantAdmin(t) || 본인(활성).
 * 쓰기:
 *   - membershipOps.*  : admin_ops 요청 문서(함수 processAdminOp / membership.ts) — 통합관리·계정 추가·정지 등
 *   - membersRepo.updateMember : 법인 관리자 직접 수정(rules members update — role/allow/deny/biz/dept/position 만,
 *                         대상이 본인·법인 관리자 아님, 현재/새 역할 모두 tenant_assignable). 이름·사본·account_active 는 함수만.
 */
import { collection, doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { db } from "./firebase";
import { runAdminOp } from "./admin_ops.service";
import { TENANT_KEYS, type BizKey, type TenantKey } from "@/domain/space";
import {
  isEmployment,
  isMemberStatus,
  parseMemberBizs,
  parseUnitRoles,
  unitRolesObject,
  type MemberUnit,
  type Employment,
  type MemberRow,
} from "@/domain/membership";
import type { DeviceKind, MemberDeviceSummary } from "@/domain/session";

function toDate(v: unknown): Date | null {
  if (v && typeof (v as { toDate?: () => Date }).toDate === "function") return (v as { toDate: () => Date }).toDate();
  return null;
}
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

function parseDevices(v: unknown): MemberDeviceSummary[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object" && typeof (x as { id?: unknown }).id === "string")
    .map((x) => ({
      id: str(x.id),
      name: str(x.name),
      kind: (x.kind === "mo" ? "mo" : "pc") as DeviceKind,
      browser: str(x.browser),
      os: str(x.os),
      last_seen_at: toDate(x.last_seen_at),
      auto_login: x.auto_login !== false,
    }));
}

/** Firestore → MemberRow. 알 수 없는 status·biz·employment 는 안전한 값으로(런타임 화이트리스트) */
export function memberFromFirestore(tenant: TenantKey, id: string, x: DocumentData): MemberRow {
  return {
    id,
    uid: id,
    tenant,
    role: str(x.role),
    allow: strList(x.allow),
    deny: strList(x.deny),
    bizs: parseMemberBizs(tenant, x.unit_roles && typeof x.unit_roles === "object" ? { bizs: Object.keys(x.unit_roles) } : x),
    units: parseUnitRoles(tenant, x),
    rank: str(x.rank),
    dept: str(x.dept),
    position: str(x.position),
    employment: isEmployment(x.employment) ? x.employment : null,
    tenantAdmin: x.tenant_admin === true,
    // 모르는 status 는 "ended" 로 — 권한을 넓히는 쪽으로 잘못 보이지 않게
    status: isMemberStatus(x.status) ? x.status : "ended",
    startDate: str(x.start_date),
    endDate: str(x.end_date),
    endAt: toDate(x.end_at),
    endReason: str(x.end_reason),
    name: str(x.name),
    loginId: str(x.login_id),
    lastLoginAt: toDate(x.last_login_at),
    devices: parseDevices(x.devices),
    accountActive: typeof x.account_active === "boolean" ? x.account_active : null,
    protected: typeof x.protected === "boolean" ? x.protected : null,
  };
}

export interface MyMembershipSnapshot {
  /** 문서가 있는 소속(종료된 것 포함) */
  rows: MemberRow[];
  /** 한 법인이라도 소속 문서가 있었는가 — 없으면 옛 계정(소속 문서 도입 전)으로 본다 */
  anyDoc: boolean;
}

export const membersRepo = {
  /** 한 법인의 소속 전체(종료 포함). */
  watchTenant(
    tenant: TenantKey,
    cb: (rows: MemberRow[]) => void,
    onError?: (e: Error) => void,
  ): () => void {
    return onSnapshot(
      collection(db, "tenants", tenant, "members"),
      (snap) => cb(snap.docs.map((d) => memberFromFirestore(tenant, d.id, d.data()))),
      onError,
    );
  },

  /** 두 법인 소속 전체 — 구독 2개. 통합관리 목록(B-1)·관리자 지정 화면용. */
  watchAll(cb: (rows: MemberRow[]) => void, onError?: (e: Error) => void): () => void {
    const parts = new Map<TenantKey, MemberRow[]>();
    const emit = () => {
      if (parts.size < TENANT_KEYS.length) return; // 둘 다 첫 응답이 온 뒤에만
      cb(TENANT_KEYS.flatMap((t) => parts.get(t) ?? []));
    };
    const unsubs = TENANT_KEYS.map((t) =>
      membersRepo.watchTenant(t, (rows) => { parts.set(t, rows); emit(); }, onError),
    );
    return () => unsubs.forEach((u) => u());
  },

  /** 소속 문서 1건 구독(없으면 null) — 내 계정·환경 머리·회원정보(useMyProfile). 읽기 거부는 onError 로 */
  watchOne(
    tenant: TenantKey,
    uid: string,
    cb: (m: MemberRow | null) => void,
    onError?: (e: Error) => void,
  ): () => void {
    return onSnapshot(
      doc(db, "tenants", tenant, "members", uid),
      (s) => cb(s.exists() ? memberFromFirestore(tenant, s.id, s.data()) : null),
      onError,
    );
  },

  /** 한 사람의 법인별 소속 문서(없으면 그 법인 키가 빠짐) — 상세 모달(B-3)용. 구독 2개. */
  watchUser(uid: string, cb: (rows: MemberRow[]) => void, onError?: (e: Error) => void): () => void {
    const parts = new Map<TenantKey, MemberRow | null>();
    const emit = () => {
      if (parts.size < TENANT_KEYS.length) return;
      cb(TENANT_KEYS.map((t) => parts.get(t)).filter((m): m is MemberRow => !!m));
    };
    const unsubs = TENANT_KEYS.map((t) =>
      onSnapshot(
        doc(db, "tenants", t, "members", uid),
        (s) => { parts.set(t, s.exists() ? memberFromFirestore(t, s.id, s.data()) : null); emit(); },
        onError,
      ),
    );
    return () => unsubs.forEach((u) => u());
  },

  /**
   * 내 소속 문서 구독(두 법인) — SpaceContext 가 근무 법인·법인 관리자 판정에 쓴다.
   * 모든 법인의 첫 응답이 온 뒤부터 cb 를 부른다. 읽기 거부(비활성 계정 등)는 "문서 없음"으로 본다 — 공간을 열지 않는 쪽이 안전.
   */
  watchMine(uid: string, cb: (snap: MyMembershipSnapshot) => void): () => void {
    const state = new Map<TenantKey, MemberRow | null>();
    const emit = () => {
      if (state.size < TENANT_KEYS.length) return;
      const rows = TENANT_KEYS.map((t) => state.get(t)).filter((m): m is MemberRow => !!m);
      cb({ rows, anyDoc: rows.length > 0 });
    };
    const unsubs = TENANT_KEYS.map((t) =>
      onSnapshot(
        doc(db, "tenants", t, "members", uid),
        (s) => { state.set(t, s.exists() ? memberFromFirestore(t, s.id, s.data()) : null); emit(); },
        () => { state.set(t, null); emit(); },
      ),
    );
    return () => unsubs.forEach((u) => u());
  },

  // (2026-10-26) 부서·사업부의 rules 직접 수정(updateMember)은 없앴다 — membershipOps.setUnits(함수 경유)로 일원화.

  // 법인 관리자 배정 가능 부서는 사업부 부서 문서 tenant_assignable(hooks/useRoles, 12 §16-2)

};

// ── 소속 변경 요청 (admin_ops → processAdminOp / membership.ts) ─────────────────
// 파라미터 이름은 backend/functions/src/membership.ts parseTarget()(uid·tenant) 기준.

export interface MembershipAddInput {
  uid: string;
  tenant: TenantKey;
  /** 사업부별 부서(2026-10-26) — 함수가 role(권한용)·bizs 를 파생해 씀 */
  units: MemberUnit[];
  employment: Employment;
  /** YYYY-MM-DD (KST) */
  startDate: string;
}

export const membershipOps = {
  add(i: MembershipAddInput) {
    return runAdminOp("membership.add", {
      uid: i.uid, tenant: i.tenant, unit_roles: unitRolesObject(i.units), employment: i.employment,
      start_at: i.startDate,
    });
  },
  /** 종료일(포함)·사유 필수. 종료일이 앞날이면 그날까지 유지(함수가 처리). */
  end(uid: string, tenant: TenantKey, endDate: string, reason: string) {
    return runAdminOp("membership.end", { uid, tenant, end_at: endDate, reason });
  },
  setTenantAdmin(uid: string, tenant: TenantKey, value: boolean) {
    return runAdminOp("membership.setTenantAdmin", { uid, tenant, value });
  },
  setRole(uid: string, tenant: TenantKey, role: string) {
    return runAdminOp("membership.setRole", { uid, tenant, role });
  },
  /** 사업부 다중 — {uid, tenant, bizs} (호환 — 함수가 setUnits 와 같은 쓰기로 변환). 새 화면은 setUnits */
  setBiz(uid: string, tenant: TenantKey, bizs: BizKey[]) {
    return runAdminOp("membership.setBiz", { uid, tenant, bizs });
  },
  /**
   * 사업부별 부서 한 번에 — {uid, tenant, unit_roles}. 법인 관리자도 가능(함수가 지금·새 부서 모두 배정 가능인지 검사).
   * 함수가 role(권한용 — 첫 운영 중 사업부의 부서)·bizs 파생값을 함께 쓴다. 부서·사업부 변경은 이 경로로 일원화.
   */
  setUnits(uid: string, tenant: TenantKey, units: readonly MemberUnit[]) {
    return runAdminOp("membership.setUnits", { uid, tenant, unit_roles: unitRolesObject(units) });
  },
  // 직위(position)는 화면에서 뺐다(통합 직급 users.rank 로 대체, 값은 남김) — setProfile 호출부 없음
};
